import { useCallback, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'rr-theme';

function readStored(): Theme | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark') return v;
  } catch {
    // private mode / locked storage — fall through to system pref
  }
  return null;
}

function systemPref(): Theme {
  return typeof window !== 'undefined' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

/**
 * Theme state synced to <html class="dark"> + localStorage.
 *
 * Initial value matches the FOUC-prevention script in index.html so the
 * first render doesn't disagree with what the browser already painted.
 * If the user hasn't picked a theme in this app, system preference wins
 * AND continues to drive the UI (live media-query subscription).
 */
export function useTheme(): {
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggleTheme: () => void;
} {
  const [theme, setThemeState] = useState<Theme>(() => readStored() ?? systemPref());

  // Push the resolved theme onto <html> + persist. Both happen on every
  // change so a manual toggle in one tab won't drift from a system change
  // in another.
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // ignore — UI still works, preference just won't survive reload
    }
  }, [theme]);

  // Track system preference changes ONLY when the user hasn't made an
  // explicit choice — once they click the toggle, their choice sticks
  // even if the OS theme flips later.
  useEffect(() => {
    if (readStored() !== null) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent): void => {
      setThemeState(e.matches ? 'dark' : 'light');
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const setTheme = useCallback((t: Theme): void => setThemeState(t), []);
  const toggleTheme = useCallback(
    (): void => setThemeState((curr) => (curr === 'dark' ? 'light' : 'dark')),
    [],
  );

  return { theme, setTheme, toggleTheme };
}
